import { isOptedIntoNewExperience } from '@/Client/Lib/newExperienceLib';
/** Deep import, not the FocusPanel barrel: that barrel re-exports panels that import `kaiNavigation`, which would cycle State ↔ Partials. */
import { SCAN_FOCUS_PANEL_PAGE_ORDER, type ScanFocusPanelPage } from '@/Client/Partials/FocusPanel/scanFocusPanelTypes';
import { useFeatureFlags } from '@kaiizen/kai-pro-api-react-state';
import { getAllTasksById, getScanGroupOrUndefined, getScanTaskOrUndefined, ScanRootOptimized } from '@kaiizen/kai-scan-template-system';
import { useCallback } from 'react';
import { useLocation } from 'react-router';

import { useGuardedNavigate } from './NavigationGuardContext';

export type { ScanFocusPanelPage };

export const useNavigateToDashboard = () => {
  const navigate = useGuardedNavigate();
  return useCallback(({ marketId }: { marketId?: string } = {}) => navigate(marketId ? `/dashboard/market/${marketId}` : '/dashboard'), [navigate]);
};

export const useNavigateToProperties = () => {
  const navigate = useGuardedNavigate();
  return useCallback(({ id }: { id?: string } = {}) => navigate(id ? `/properties/${id}` : '/properties'), [navigate]);
};

export const useNavigateToMarkets = () => {
  const navigate = useGuardedNavigate();
  return useCallback(({ id }: { id?: string } = {}) => navigate(id ? `/market/${id}` : '/markets'), [navigate]);
};

export const useNavigateToUsers = () => {
  const navigate = useGuardedNavigate();
  return useCallback(({ id }: { id?: string } = {}) => navigate(id ? `/user/${id}` : '/users'), [navigate]);
};

export const useNavigateToContractors = () => {
  const navigate = useGuardedNavigate();
  return useCallback(({ id }: { id?: string } = {}) => navigate(id ? `/contractor/${id}` : '/contractors'), [navigate]);
};

export const useNavigateToProducts = () => {
  const navigate = useGuardedNavigate();
  return useCallback(({ id }: { id?: string } = {}) => navigate(id ? `/product/${id}` : '/products'), [navigate]);
};

export const useNavigateToPhoto = () => {
  const navigate = useGuardedNavigate();
  const { pathname, search, hash } = useLocation();
  return useCallback(({ photoId, groupId, taskId, date }: { photoId: string; groupId?: string; taskId?: string; date?: string }) => {
    console.log(`useNavigateToPhoto: photoId: ${photoId}, groupId: ${groupId}, taskId: ${taskId}`);
    const queryParams = new URLSearchParams(search);
    queryParams.set('photoId', photoId);
    if (groupId) {
      queryParams.set('photosGroupId', groupId);
    }
    if (taskId) {
      queryParams.set('photosTaskId', taskId);
    }
    if (date) {
      queryParams.set('photosDate', date);
    }
    navigate(`${pathname}?${queryParams.toString()}${hash}`);
  }, [hash, navigate, pathname, search]);
};

export type ScanUrlParts = {
  // paths
  scanId: string;
  groupId?: string;
  /**
   * Org contractor id, for the navigator's Contractor tab. Mutually exclusive
   * with groupId — they are two ways of bucketing the same tasks, so a route
   * carries one or the other, never both.
   */
  contractorId?: string;
  taskId?: string;

  // specific pages
  review?: boolean;
  focusPanel?: ScanFocusPanelPage;
  progress?: boolean;
  scope?: boolean;
  punchList?: boolean;
  closeout?: boolean;
  changeOrder?: boolean;
  artifactList?: boolean;
  artifactScanId?: string;
  artifactDocument?: 'changeOrder' | 'fullScope' | 'closeout';

  // or
  page?: 'review' | 'progress' | 'scope' | 'photos' | 'punchList' | 'closeout' | 'changeOrder' | 'group';
  newScopeBuilderFlow?: boolean;
  // navigation options
  replace?: boolean;
};

const buildScanFocusPanelPath = (
  scanId: string,
  segment: ScanFocusPanelPage,
  groupId: string | undefined,
  taskId: string | undefined,
  queryString: string,
) => {
  if (groupId && taskId) {
    return `/scan/${scanId}/group/${groupId}/task/${taskId}/${segment}${queryString}`;
  }
  if (groupId) {
    return `/scan/${scanId}/group/${groupId}/${segment}${queryString}`;
  }
  return `/scan/${scanId}/${segment}${queryString}`;
};

export const getScanUrl = ({
  scanId,
  groupId,
  contractorId,
  taskId,
  review,
  focusPanel,
  scope,
  progress,
  punchList,
  closeout,
  changeOrder,
  artifactList,
  artifactScanId,
  artifactDocument,
  page,
  newScopeBuilderFlow,
}: ScanUrlParts) => {
  let resolvedFocusPanel: ScanFocusPanelPage | undefined = focusPanel;

  if (page) {
    switch (page) {
      case 'review':
        review = true;
        break;
      case 'progress':
        progress = true;
        break;
      case 'scope':
        scope = true;
        break;
      case 'photos':
        resolvedFocusPanel = 'photos';
        break;
      case 'punchList':
        punchList = true;
        break;
      case 'closeout':
        closeout = true;
        break;
      case 'changeOrder':
        changeOrder = true;
        break;
    }
  }
  // Project Overview has no group, task, or contractor route. Discarding a
  // stale selection here keeps generic focus-panel navigation from producing
  // unregistered URLs such as `/group/:groupId/overview`.
  if (resolvedFocusPanel === 'overview') {
    groupId = undefined;
    contractorId = undefined;
    taskId = undefined;
  }
  page = scope
    ? 'scope'
    : punchList
    ? 'punchList'
    : closeout
    ? 'closeout'
    : changeOrder
    ? 'changeOrder'
    : progress
    ? 'progress'
    : resolvedFocusPanel === 'photos'
    ? 'photos'
    : groupId
    ? 'group'
    : 'review';

  if (page === 'group' && !groupId) page = 'review';
  const queryParams = new URLSearchParams();
  if (artifactList) queryParams.set('artifacts', 'list');
  let queryString = queryParams.toString();
  if (queryString) queryString = `?${queryString}`;

  if (resolvedFocusPanel === 'artifact' && artifactScanId) {
    return `/scan/${scanId}/artifact/${artifactScanId}${artifactDocument ? `?artifactDocument=${artifactDocument}` : ''}`;
  }

  /* Contractor-bucketed selection. Mirrors the group path exactly, so a task
     keeps the same URL shape whichever tab the user reached it through —
     including the focus-panel segment, so drilling into a task under a
     contractor lands on a focus panel exactly like drilling into it under a
     group does. */
  if (contractorId && !groupId) {
    const contractorPath = `/scan/${scanId}/contractor/${contractorId}`;
    if (resolvedFocusPanel) {
      return taskId
        ? `${contractorPath}/task/${taskId}/${resolvedFocusPanel}${queryString}`
        : `${contractorPath}/${resolvedFocusPanel}${queryString}`;
    }
    return taskId ? `${contractorPath}/task/${taskId}${queryString}` : `${contractorPath}${queryString}`;
  }

  if (progress) {
    if (groupId && taskId) {
      return `/scan/${scanId}/progress/group/${groupId}/task/${taskId}${queryString}`;
    }
    if (groupId) {
      return `/scan/${scanId}/progress/group/${groupId}${queryString}`;
    }
    return `/scan/${scanId}/progress${queryString}`;
  }

  if (resolvedFocusPanel) {
    return buildScanFocusPanelPath(scanId, resolvedFocusPanel, groupId, taskId, queryString);
  }

  if (groupId && taskId) {
    return `/scan/${scanId}/group/${groupId}/task/${taskId}${queryString}`;
  }

  if (taskId) {
    queryParams.set('taskId', taskId);
    queryString = `?${queryParams.toString()}`;
  }

  switch (page) {
    case 'group':
      return `/scan/${scanId}/group/${groupId}${queryString}`;
    case 'changeOrder':
      return `/scan/${scanId}/change-order${queryString}`;
    case 'closeout':
      return `/scan/${scanId}/closeout${queryString}`;
    case 'photos':
      return `/scan/${scanId}/photos${queryString}`;
    case 'progress':
      return `/scan/${scanId}/progress${queryString}`;
    case 'punchList':
      return `/scan/${scanId}/punchlist${queryString}`;
    case 'scope':
      return `/scan/${scanId}/scope`;

    // review is the default
    default:
      return newScopeBuilderFlow ? `/scan/${scanId}` : `/scan/${scanId}/review${queryString}`;
  }
};

export const getValidatedScanUrlParts = ({ scanRoot, ...scanUrlParts }: { scanRoot?: ScanRootOptimized } & ScanUrlParts): ScanUrlParts => {
  const { groupId, contractorId, taskId } = scanUrlParts;
  if (!scanRoot) return { scanId: scanUrlParts.scanId };
  const validatedGroup = getScanGroupOrUndefined(scanRoot, groupId);
  /* A contractor route names no group, so the task has to be looked for across
     the whole scan rather than inside one. Validating it against a group that
     isn't there would silently drop it from the URL. */
  const validatedTask = contractorId && !validatedGroup
    ? getAllTasksById(scanRoot)[taskId ?? '']
    : getScanTaskOrUndefined(scanRoot, validatedGroup?.id, taskId);
  return {
    ...scanUrlParts,
    groupId: validatedGroup?.id,
    taskId: validatedTask?.id,
  };
};

/**
 * Returns the ScanUrlParts that are one page closer to the root scan page.
 * @param scanUrlParts - the scan url parts
 * @returns the parent scan url parts
 */
export const getScanUrlParent = ({
  scanId,
  groupId,
  contractorId,
  taskId,
  review,
  focusPanel,
  scope,
}: ScanUrlParts): ScanUrlParts => {
  const resolvedFocusPanel = focusPanel;
  if (scope || review) return { scanId, groupId, contractorId, taskId };
  if (resolvedFocusPanel === 'overview') return { scanId, focusPanel: resolvedFocusPanel };
  if (resolvedFocusPanel) {
    if (taskId) {
      return { scanId, groupId, contractorId, focusPanel: resolvedFocusPanel };
    }
    if (groupId || contractorId) {
      return { scanId, groupId, contractorId, focusPanel: resolvedFocusPanel };
    }
    return { scanId, focusPanel: resolvedFocusPanel };
  }
  if (taskId) return { scanId, groupId, contractorId };
  return { scanId };
};

const useNewScopeBuilderFlow = () => {
  const { featureFlags } = useFeatureFlags();
  return isOptedIntoNewExperience(featureFlags);
};

const dropPageParameterIfNewScopeBuilderFlow = (
  params: ScanUrlParts,
  newScopeBuilderFlow: boolean,
): ScanUrlParts => newScopeBuilderFlow && params.page ? { ...params, page: undefined } : params;

/**
 * The query params a path rewrite must NOT carry, because each of them addresses the very thing the
 * rewrite is replacing. Everything else rides along.
 *
 * `photoId` and its companions are the large photo viewer's address, and `AdminScanPageNew` opens that
 * viewer on sight of `photoId` alone. Closing it is a navigation to the same scope without it, so
 * carrying it would be how you get a viewer that cannot be shut. `taskId` named the selected task back
 * before the path had a slot for it; carried into a route that names its own scope, it puts the old
 * task back beside the new one.
 */
const SCAN_SEARCH_PARAMS_NOT_CARRIED = [
  'artifacts',
  'artifactDocument',
  'artifactScanId',
  'photoId',
  'photosGroupId',
  'photosTaskId',
  'photosDate',
  'taskId',
] as const;

/** Extracts the scanId from a scan workspace pathname (`/scan/:scanId/...`). */
export const getScanIdFromPathname = (pathname: string): string | undefined => /\/scan\/([^/?#]+)/.exec(pathname)?.[1];

/**
 * Reads the scope and focus-panel slots encoded in a scan workspace pathname.
 * Used when navigating to a sibling scan (e.g. after scope approval spawns a
 * progress child) and the destination should keep the reader where they were.
 */
export const parseScanNavigationFromPathname = (
  pathname: string,
): Pick<ScanUrlParts, 'groupId' | 'contractorId' | 'taskId' | 'focusPanel' | 'progress'> => {
  const groupId = /\/group\/([^/]+)/.exec(pathname)?.[1];
  const contractorId = groupId ? undefined : /\/contractor\/([^/]+)/.exec(pathname)?.[1];
  const taskId = /\/task\/([^/]+)/.exec(pathname)?.[1];
  const focusPanel = [...SCAN_FOCUS_PANEL_PAGE_ORDER]
    .sort((left, right) => right.length - left.length)
    .find(panel => pathname.endsWith(`/${panel}`));
  const progress = /\/scan\/[^/]+\/progress(?:\/|$)/.test(pathname);
  return {
    ...(groupId ? { groupId } : {}),
    ...(contractorId ? { contractorId } : {}),
    ...(taskId ? { taskId } : {}),
    ...(focusPanel ? { focusPanel } : {}),
    ...(progress ? { progress } : {}),
  };
};

/**
 * The destination URL with the query string it is navigating away from merged into it.
 *
 * Navigating inside the scan workspace rewrites the path and keeps the query. Attention filters, how
 * the Progress Alt grid is cut and folded, `?debug` — every one of them is something the reader set up
 * deliberately, and none of them is something picking another group or another panel should undo. So
 * the default is to carry, and `SCAN_SEARCH_PARAMS_NOT_CARRIED` is the whole of the exception.
 *
 * The destination wins every collision, being the one that knows where it is going: a param it sets
 * itself is a decision, while the same param in the current URL is only history.
 */
export const mergeScanSearchParams = (
  url: string,
  currentSearch: string,
  sameScan: boolean,
): string => {
  // Scan-scoped, all of it: carrying these into a different scan would apply the previous scan's
  // filters and address rows and photos it hasn't got, which can make the destination look empty.
  if (!sameScan) return url;
  const [path, destinationQuery = ''] = url.split('?');
  const merged = new URLSearchParams(currentSearch);
  for (const key of SCAN_SEARCH_PARAMS_NOT_CARRIED) merged.delete(key);
  for (const [key, value] of new URLSearchParams(destinationQuery)) merged.set(key, value);
  const query = merged.toString();
  return query ? `${path}?${query}` : path;
};

/**
 * Legacy `?taskId=` support for group routes only.
 *
 * Before task selection had a path slot, a group route named the task to open in the query string.
 * Those links still work for group editor routes, but once the path itself names a task, the path is
 * the whole selection and the legacy query param no longer participates.
 */
export const getLegacyExpandedTaskIdFromScanSearchParams = (
  searchParams: URLSearchParams,
  taskIdFromRoute?: string,
): string | undefined => taskIdFromRoute ? undefined : (searchParams.get('taskId') || undefined);

/**
 * Goes to a path, carrying the query string along when the path stays inside the same scan.
 *
 * The one way to move within the scan workspace, so that carrying the reader's setup is the default
 * rather than something each caller has to remember — which is how it came to be dropped on a click in
 * the navigator in the first place.
 */
const useNavigateWithinScan = () => {
  const navigate = useGuardedNavigate();
  const { pathname, search } = useLocation();
  return useCallback(
    (url: string, options?: { replace?: boolean }) => {
      const sameScan = getScanIdFromPathname(pathname) === getScanIdFromPathname(url);
      return navigate(mergeScanSearchParams(url, search, sameScan), options);
    },
    [navigate, pathname, search],
  );
};

export const useNavigateToScan = () => {
  const navigateWithinScan = useNavigateWithinScan();
  const newScopeBuilderFlow = useNewScopeBuilderFlow();
  return useCallback((params: ScanUrlParts) => {
    params = dropPageParameterIfNewScopeBuilderFlow(params, newScopeBuilderFlow);
    params.newScopeBuilderFlow = newScopeBuilderFlow;
    return navigateWithinScan(getScanUrl(params), { replace: params.replace });
  }, [navigateWithinScan, newScopeBuilderFlow]);
};

export const getScanUrlPartsForFocusPanelPage = ({
  scanId,
  panel,
  groupId,
  contractorId,
  taskId,
  replace,
}: {
  scanId: string;
  panel: ScanFocusPanelPage;
  groupId?: string;
  contractorId?: string;
  taskId?: string;
  replace?: boolean;
}): ScanUrlParts => ({ scanId, groupId, contractorId, taskId, focusPanel: panel, ...(replace === undefined ? {} : { replace }) });

export const useNavigateToScanFocusPanelPage = () => {
  const navigateToScan = useNavigateToScan();
  return useCallback(
    (params: {
      scanId: string;
      panel: ScanFocusPanelPage;
      groupId?: string;
      contractorId?: string;
      taskId?: string;
      replace?: boolean;
    }) => navigateToScan(getScanUrlPartsForFocusPanelPage(params)),
    [navigateToScan],
  );
};

/**
 * The scan pages reached by name rather than by scope. Each is a move within the workspace, so each
 * carries the query the same way selecting a group does.
 */
const useNavigateToScanPage = (segment: string) => {
  const navigateWithinScan = useNavigateWithinScan();
  const newScopeBuilderFlow = useNewScopeBuilderFlow();
  return useCallback(
    ({ scanId }: { scanId: string }) => navigateWithinScan(newScopeBuilderFlow ? `/scan/${scanId}` : `/scan/${scanId}/${segment}`),
    [navigateWithinScan, newScopeBuilderFlow, segment],
  );
};

export const useNavigateToScope = () => useNavigateToScanPage('scope');

export const useNavigateToChangeOrder = () => useNavigateToScanPage('change-order');

export const useNavigateToPunchlistScopePreview = () => useNavigateToScanPage('punchlist');

export const useNavigateToCloseoutScopePreview = () => useNavigateToScanPage('closeout');

export const useNavigateToScanPhotos = () => {
  const navigateWithinScan = useNavigateWithinScan();
  return useCallback(
    ({ scanId }: { scanId: string }) => navigateWithinScan(`/scan/${scanId}/photos`),
    [navigateWithinScan],
  );
};

export const useNavigateToScanTemplate = () => {
  const navigate = useGuardedNavigate();
  return useCallback(({
    scanTemplateId,
    groupId,
    allTasks,
    classic,
  }: {
    scanTemplateId: string;
    groupId?: string;
    allTasks?: boolean;
    classic?: boolean;
  }) =>
    navigate(
      groupId
        ? `/template/${scanTemplateId}/group/${groupId}`
        : allTasks
        ? `/template/${scanTemplateId}/all-tasks`
        : classic
        ? `/template/${scanTemplateId}/classic`
        : `/template/${scanTemplateId}`,
    ), [navigate]);
};

export const useNavigateToScanTemplates = () => {
  const navigate = useGuardedNavigate();
  return useCallback(({ scanTemplateId }: { scanTemplateId?: string } = {}) => navigate(scanTemplateId ? `/templates/${scanTemplateId}` : '/templates'), [
    navigate,
  ]);
};

export const useNavigateToAccount = () => {
  const navigate = useGuardedNavigate();
  return useCallback(() => navigate('/account'), [navigate]);
};

export const useNavigateToProperty = () => {
  const navigate = useGuardedNavigate();
  return useCallback(
    ({ propertyId, edit }: { propertyId: string; edit?: boolean }) =>
      edit
        ? navigate(`/properties/${propertyId}`)
        : navigate(`/property/${propertyId}`),
    [navigate],
  );
};

export const getProjectUrl = (projectId: string) => `/project/${projectId}`;

export const useNavigateToProject = () => {
  const navigate = useGuardedNavigate();
  return useCallback(({ projectId }: { projectId: string }) => navigate(`${getProjectUrl(projectId)}`), [navigate]);
};

export const useNavigateToProjectScan = () => {
  const navigate = useGuardedNavigate();
  return useCallback(({ projectId, scanId }: { projectId: string; scanId: string }) => navigate(`${getProjectUrl(projectId)}/scan/${scanId}`), [navigate]);
};
